<script lang="ts">
	import Input from '$lib/components/Inputs/Input.svelte';
	import TextArea from '$lib/components/Inputs/TextArea.svelte';
	import Modal from '$lib/components/Modal/Modal.svelte';
	import { _ } from 'svelte-i18n';
	import { addPreviewImages, isWidgetModalOpen, widgetPost } from './widgetHelpers';
	import Footer from '$textEditor/PostComposer/Footer.svelte';
	import Button from '$lib/components/Button/Button.svelte';
	import { getHubStores } from '$lib/stores/hubStores';
	import { toastMessages } from '$lib/stores/toastMessageStore';
	import { atLeastOrgAdmin } from '$lib/utils/roles';
	import Select from 'svelte-select';
	const { hub, api } = getHubStores();

	const widgetItems = [
		{ label: 'Quick poll', value: 'widgetQuickPoll' },
		{ label: $_('hub.settings.cta.widget.iframe'), value: 'widgetLink' },
	];

	if (!$hub.settings.feature.disable_custom_html || atLeastOrgAdmin($hub.user_context)) {
		widgetItems.push({ label: $_('hub.settings.cta.widget.iframe_in_adtag'), value: 'widgetHtml' });
	}

	const getSelectValue = () => {
		return widgetItems.find((item) => item.value == $widgetPost.type);
	};

	const getPreviewImage = async () => {
		try {
			if (!$widgetPost.etc.widget.url) throw new Error('No url found');
			const resp = await api.embedLink({ hubId: $hub.id, url: $widgetPost.etc.widget.url });
			if (!resp.asset.title_image?.url) throw new Error('No image found');
			addPreviewImages([resp.asset.title_image.url]);
		} catch (e) {
			toastMessages.danger('', e);
		}
	};

	const beforeSave = async () => {
		if ($widgetPost.type == 'widgetQuickPoll' || $widgetPost.type == 'widgetLink') {
			$widgetPost.etc.widget.url = $widgetPost.etc.widget.url?.trim() || '';
			if (!$widgetPost.etc.widget.url.includes('.')) {
				toastMessages.danger($_('hub.settings.sign.in.join.url.error'));
				throw new Error('Invalid url');
			} else if (!/^(http|https):\/\//.test($widgetPost.etc.widget.url)) {
				$widgetPost.etc.widget.url = `https://${$widgetPost.etc.widget.url}`;
			}
		}
		if ($widgetPost.type == 'widgetLink') {
			if (!$widgetPost.title_image.url) {
				await getPreviewImage();
			}
		}
	};

	const fetchTouchpointQuickPollLinks = async () => {
		try {
			const links = await api.getTouchpointQuickPollLinks({ hubId: $hub.id });
			return links.hubsQuickPollLinks.map((link) => ({
				value: link.url,
				label: `${link.name} - ${link.url}`,
			}));
		} catch (e) {
			toastMessages.danger('', e);
		}
	};

	const afterSave = async () => {
		$isWidgetModalOpen = false;
	};
</script>

<Modal
	bind:isOpen={$isWidgetModalOpen}
	onClose={() => {
		$isWidgetModalOpen = false;
	}}
>
	<h1 slot="title">{$widgetPost.status === 'published' ? '' : $_('hub.settings.widgets.new')}</h1>
	<div class="modalInputFields" slot="content">
		<div class="container">
			<Input
				required
				type="text"
				name="widgetName"
				label={$_('hub.settings.widget.name')}
				bind:value={$widgetPost.title}
			/>
			<label for="widgetType"
				>{$_('hub.settings.cta.widget.select.type')}
				<Select
					searchable={false}
					showChevron={true}
					clearable={false}
					value={getSelectValue()}
					bind:justValue={$widgetPost.type}
					items={widgetItems}
					name="widgetType"
					disabled={!!$widgetPost.id}
				/>
			</label>
			{#if $widgetPost.type === 'widgetHtml'}
				<TextArea
					required
					name="widgetValue"
					label="HTML"
					bind:value={$widgetPost.etc.widget.html}
				/>
			{:else if $widgetPost.type == 'widgetLink'}
				<div class="widgetUrl">
					<Input
						required
						type="text"
						name="widgetValue"
						label={$_('common.url')}
						bind:value={$widgetPost.etc.widget.url}
					/>
					<div class="getImageButton">
						<Button displayType="primary" on:click={getPreviewImage}
							>{$_('title.image.get.preview')}</Button
						>
					</div>
				</div>
			{:else if $widgetPost.type == 'widgetQuickPoll'}
				{#await fetchTouchpointQuickPollLinks() then links}
					<div>
						<Select
							bind:justValue={$widgetPost.etc.widget.url}
							value={$widgetPost.etc.widget.url}
							name="widgetValue"
							items={links || []}
						/>
					</div>
				{/await}
			{/if}
		</div>
		<Footer
			post={widgetPost}
			{beforeSave}
			{afterSave}
			showPreviewImg={$widgetPost.type == 'widgetLink'}
		/>
	</div>
</Modal>

<style>
	h1 {
		margin: 0;
	}

	.modalInputFields {
		inline-size: 800px;
	}

	label {
		font-size: var(--fontSizeSmall);
	}

	.container {
		display: grid;
		grid-template-columns: 1fr;
		padding: var(--gutterDefault);
		gap: var(--gutterDefault);
	}

	.widgetUrl {
		display: grid;
		grid-template-columns: 1fr auto;
		align-items: end;
	}

	.getImageButton {
		margin-block-end: var(--gutterSmallest);
		margin-inline-start: var(--gutterDefault);
	}
</style>
